// ============================================================
//  src/pages/SignInPage.jsx
//  Login & Registration — connected to Supabase via useAuth
//  Updated: Google Fonts (DM Sans + Playfair Display) + Material Icons
// ============================================================

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Button, ErrorMessage } from "../components/UI";
import config from "../config";

// ──────────────────────────────────────────────────────────
// Inject Google Fonts + Material Icons into <head>
// ──────────────────────────────────────────────────────────
function useGoogleAssets() {
  useEffect(() => {
    const links = [
      {
        rel: "preconnect",
        href: "https://fonts.googleapis.com",
      },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,700;9..40,800&family=Playfair+Display:wght@700;800&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/icon?family=Material+Icons+Round",
      },
    ];

    const injected = [];
    links.forEach(({ rel, href, crossOrigin }) => {
      if (document.querySelector(`link[href="${href}"]`)) return;
      const el = document.createElement("link");
      el.rel = href.includes("gstatic") || href.includes("googleapis.com") && !href.includes("css") ? "preconnect" : rel;
      el.rel = rel;
      el.href = href;
      if (crossOrigin) el.crossOrigin = crossOrigin;
      document.head.appendChild(el);
      injected.push(el);
    });

    return () => injected.forEach((el) => el.remove());
  }, []);
}

// ──────────────────────────────────────────────────────────
// Material Icon helper
// ──────────────────────────────────────────────────────────
function Icon({ name, style = {} }) {
  return (
    <span
      className="material-icons-round"
      style={{ fontSize: 20, verticalAlign: "middle", lineHeight: 1, ...style }}
    >
      {name}
    </span>
  );
}

// ──────────────────────────────────────────────────────────
// Field-level validators
// ──────────────────────────────────────────────────────────
const validators = {
  name(value) {
    if (!value.trim())                          return "Full name is required.";
    if (value.trim().length < 5)                return "Name must be at least 5 characters.";
    if (value.trim().length > 50)               return "Name must be under 50 characters.";
    if (!/^[a-zA-Z\s'-]+$/.test(value.trim())) return "Name can only contain letters, spaces, hyphens, or apostrophes.";
    return "";
  },
  email(value) {
    if (!value.trim())                                              return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()))         return "Please enter a valid email address.";
    return "";
  },
  phone(value) {
    if (!value.trim())                           return "Phone number is required.";
    const digits = value.replace(/\D/g, "");
    if (digits.length < 10)                      return "Phone number must be at least 10 digits.";
    if (digits.length > 15)                      return "Phone number must be under 15 digits.";
    if (!/^[+\d\s\-()]+$/.test(value.trim()))   return "Please enter a valid phone number.";
    return "";
  },
  password(value) {
    if (!value)           return "Password is required.";
    if (value.length < 6) return "Password must be at least 6 characters.";
    return "";
  },
  confirm(value, password) {
    if (!value)             return "Please confirm your password.";
    if (value !== password) return "Passwords do not match.";
    return "";
  },
};

// ──────────────────────────────────────────────────────────
// Inline field error component
// ──────────────────────────────────────────────────────────
function FieldError({ message }) {
  if (!message) return null;
  return (
    <div style={{
      fontSize: 11,
      color: "#dc2626",
      marginTop: -8,
      marginBottom: 10,
      paddingLeft: 4,
      display: "flex",
      alignItems: "center",
      gap: 4,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <Icon name="warning_amber" style={{ fontSize: 13, color: "#dc2626" }} />
      {message}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Styled Input with validation state
// ──────────────────────────────────────────────────────────
function ValidatedInput({ label, type = "text", placeholder, value, onChange, onBlur, error, touched }) {
  const borderColor = touched ? (error ? "#dc2626" : "#16a34a") : "#e5e7eb";
  const bgColor     = touched ? (error ? "#fef2f2" : "#f0fdf4") : "#f9fafb";

  return (
    <div style={{ marginBottom: error && touched ? 4 : 14 }}>
      <label style={{
        fontSize: 11,
        fontWeight: 700,
        color: "#555",
        letterSpacing: 0.8,
        display: "block",
        marginBottom: 5,
        fontFamily: "'DM Sans', sans-serif",
        textTransform: "uppercase",
      }}>
        {label}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        style={{
          width: "100%",
          padding: "11px 13px",
          fontSize: 14,
          border: `1.5px solid ${borderColor}`,
          borderRadius: 10,
          background: bgColor,
          outline: "none",
          boxSizing: "border-box",
          transition: "border-color 0.2s, background 0.2s",
          fontFamily: "'DM Sans', sans-serif",
          color: "#1a1a1a",
        }}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Main Component
// ──────────────────────────────────────────────────────────
export default function SignInPage({ auth }) {
  const navigate = useNavigate();
  useGoogleAssets();

  const [mode, setMode]               = useState("signin");
  const [form, setForm]               = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
  const [touched, setTouched]         = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [localError, setLocalError]   = useState("");
  const [successMsg, setSuccessMsg]   = useState("");
  const [loading, setLoading]         = useState(false);
  const submitting                    = useRef(false);

  // ── Validate a single field ────────────────────────────
  function validateField(key, value) {
    if (key === "confirm") return validators.confirm(value, form.password);
    if (validators[key])   return validators[key](value);
    return "";
  }

  // ── On change ─────────────────────────────────────────
  function handleChange(key) {
    return (e) => {
      const value = e.target.value;
      auth.clearError();
      setLocalError("");
      setSuccessMsg("");
      setForm((f) => ({ ...f, [key]: value }));

      if (touched[key]) {
        setFieldErrors((prev) => ({ ...prev, [key]: validateField(key, value) }));
      }

      if (key === "password" && touched["confirm"]) {
        setFieldErrors((prev) => ({ ...prev, confirm: validators.confirm(form.confirm, value) }));
      }
    };
  }

  // ── On blur ───────────────────────────────────────────
  function handleBlur(key) {
    return () => {
      setTouched((prev) => ({ ...prev, [key]: true }));
      setFieldErrors((prev) => ({ ...prev, [key]: validateField(key, form[key]) }));
    };
  }

  // ── Validate ALL fields ───────────────────────────────
  function validateAll() {
    if (mode === "signin") {
      const errs = {
        email:    validators.email(form.email),
        password: validators.password(form.password),
      };
      setFieldErrors(errs);
      setTouched({ email: true, password: true });
      return Object.values(errs).every((e) => !e);
    }

    const errs = {
      name:     validators.name(form.name),
      email:    validators.email(form.email),
      phone:    validators.phone(form.phone),
      password: validators.password(form.password),
      confirm:  validators.confirm(form.confirm, form.password),
    };
    setFieldErrors(errs);
    setTouched({ name: true, email: true, phone: true, password: true, confirm: true });
    return Object.values(errs).every((e) => !e);
  }

  // ── Submit ────────────────────────────────────────────
  async function handleSubmit() {
    if (submitting.current) return;
    submitting.current = true;

    const isValid = validateAll();
    if (!isValid) { submitting.current = false; return; }

    setLoading(true);
    try {
      if (mode === "signin") {
        await auth.signIn({ email: form.email, password: form.password });
        navigate("/");
      } else {
        await auth.signUp({ name: form.name, email: form.email, phone: form.phone, password: form.password });
        await auth.logout();
        switchMode("signin");
        setSuccessMsg("Account created! Please sign in with your email.");
      }
    } catch (err) {
      setLocalError(err.message);
    } finally {
      setLoading(false);
      submitting.current = false;
    }
  }

  // ── Switch mode ───────────────────────────────────────
  function switchMode(m) {
    setMode(m);
    setForm({ name: "", email: "", phone: "", password: "", confirm: "" });
    setFieldErrors({});
    setTouched({});
    setLocalError("");
    auth.clearError();
  }

  function handleSwitchMode(m) {
    setSuccessMsg("");
    switchMode(m);
  }

  const displayError = localError || auth.error;

  return (
    <div style={{
      padding: "24px 16px 100px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{ width: "100%", maxWidth: 420 }}>

        {/* ── Header ── */}
        <div style={{
          background: `linear-gradient(135deg, ${config.colors.primary}, ${config.colors.primaryLight})`,
          borderRadius: "18px 18px 0 0",
          padding: "26px 22px",
          color: "#fff",
        }}>
          <div style={{ marginBottom: 10 }}>
            <Icon
              name={mode === "signin" ? "lock" : "eco"}
              style={{ fontSize: 38, color: "rgba(255,255,255,0.95)" }}
            />
          </div>

          {/* Playfair Display for the heading */}
          <div style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 24,
            fontWeight: 800,
            letterSpacing: 0.2,
          }}>
            {mode === "signin" ? "Welcome Back!" : "Create Account"}
          </div>

          <div style={{ opacity: 0.82, fontSize: 13, marginTop: 5, fontFamily: "'DM Sans', sans-serif" }}>
            {mode === "signin" ? "Sign in to continue shopping" : "Join RK Farm Foods today"}
          </div>
        </div>

        {/* ── Contact button ── */}
        <button
          onClick={() => navigate("/contact")}
          style={{
            width: "100%",
            marginTop: 10,
            background: "none",
            border: "none",
            color: config.colors.primary,
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            padding: "8px 0",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          <Icon name="phone" style={{ fontSize: 16, color: config.colors.primary }} />
          Need help? Contact Us
        </button>

        {/* ── Form body ── */}
        <div style={{
          background: "#fff",
          borderRadius: "0 0 18px 18px",
          padding: "22px 20px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.10)",
        }}>

          {/* Success banner */}
          {successMsg && (
            <div style={{
              background: "#f0fdf4",
              border: "1px solid #86efac",
              borderRadius: 10,
              padding: "12px 14px",
              fontSize: 13,
              color: "#166534",
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontFamily: "'DM Sans', sans-serif",
            }}>
              <Icon name="check_circle" style={{ fontSize: 18, color: "#16a34a" }} />
              {successMsg}
            </div>
          )}

          {/* ── FULL NAME (signup only) ── */}
          {mode === "signup" && (
            <>
              <ValidatedInput
                label="Full Name *"
                placeholder="Your full name"
                value={form.name}
                onChange={handleChange("name")}
                onBlur={handleBlur("name")}
                error={fieldErrors.name}
                touched={touched.name}
              />
              <FieldError message={touched.name && fieldErrors.name} />
            </>
          )}

          {/* ── EMAIL ── */}
          <>
            <ValidatedInput
              label="Email *"
              type="email"
              placeholder="you@email.com"
              value={form.email}
              onChange={handleChange("email")}
              onBlur={handleBlur("email")}
              error={fieldErrors.email}
              touched={touched.email}
            />
            <FieldError message={touched.email && fieldErrors.email} />
          </>

          {/* ── PHONE (signup only) ── */}
          {mode === "signup" && (
            <>
              <ValidatedInput
                label="Phone Number *"
                type="tel"
                placeholder="+91 98765 43210"
                value={form.phone}
                onChange={handleChange("phone")}
                onBlur={handleBlur("phone")}
                error={fieldErrors.phone}
                touched={touched.phone}
              />
              <FieldError message={touched.phone && fieldErrors.phone} />
            </>
          )}

          {/* ── PASSWORD ── */}
          <>
            <ValidatedInput
              label="Password *"
              type="password"
              placeholder={mode === "signup" ? "Min. 6 characters" : "••••••••"}
              value={form.password}
              onChange={handleChange("password")}
              onBlur={handleBlur("password")}
              error={fieldErrors.password}
              touched={touched.password}
            />
            <FieldError message={touched.password && fieldErrors.password} />
          </>

          {/* ── CONFIRM PASSWORD (signup only) ── */}
          {mode === "signup" && (
            <>
              <ValidatedInput
                label="Confirm Password *"
                type="password"
                placeholder="Re-enter password"
                value={form.confirm}
                onChange={handleChange("confirm")}
                onBlur={handleBlur("confirm")}
                error={fieldErrors.confirm}
                touched={touched.confirm}
              />
              <FieldError message={touched.confirm && fieldErrors.confirm} />
            </>
          )}

          {/* Auth error */}
          {displayError && (
            <div style={{
              background: "#fef2f2",
              border: "1px solid #fca5a5",
              borderRadius: 10,
              padding: "10px 13px",
              fontSize: 13,
              color: "#dc2626",
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontFamily: "'DM Sans', sans-serif",
            }}>
              <Icon name="error_outline" style={{ fontSize: 16, color: "#dc2626" }} />
              {displayError}
            </div>
          )}

          {/* ── Submit button ── */}
          <button
            className="btn-press"
            onClick={handleSubmit}
            disabled={loading}
            style={{
              width: "100%",
              background: loading ? "#bbb" : config.colors.primary,
              color: "#fff",
              border: "none",
              borderRadius: 13,
              padding: "14px 0",
              fontWeight: 800,
              fontSize: 15,
              cursor: loading ? "not-allowed" : "pointer",
              marginBottom: 12,
              marginTop: 4,
              fontFamily: "'DM Sans', sans-serif",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              transition: "background 0.2s",
            }}
          >
            {loading ? (
              <>
                <Icon name="hourglass_top" style={{ fontSize: 18, color: "#fff" }} />
                Please wait...
              </>
            ) : (
              <>
                {mode === "signin" ? "Sign In" : "Create Account"}
                <Icon name="arrow_forward" style={{ fontSize: 18, color: "#fff" }} />
              </>
            )}
          </button>

          {/* ── Switch mode ── */}
          <div style={{
            textAlign: "center",
            fontSize: 14,
            color: "#888",
            marginBottom: 12,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            {mode === "signin" ? (
              <>
                Don't have an account?{" "}
                <span
                  style={{ color: config.colors.accent, cursor: "pointer", fontWeight: 700 }}
                  onClick={() => handleSwitchMode("signup")}
                >
                  Sign Up
                </span>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <span
                  style={{ color: config.colors.accent, cursor: "pointer", fontWeight: 700 }}
                  onClick={() => handleSwitchMode("signin")}
                >
                  Sign In
                </span>
              </>
            )}
          </div>
        </div>

        {/* ── Security note ── */}
        <div style={{
          marginTop: 14,
          padding: "11px 14px",
          background: "#f0f7ee",
          borderRadius: 12,
          fontSize: 12,
          color: "#777",
          lineHeight: 1.6,
          border: `1px solid ${config.colors.cardBorder}`,
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          <Icon name="verified_user" style={{ fontSize: 15, color: "#4a7c59", marginTop: 1 }} />
          Your account is stored securely. Your session will auto-logout after a period of inactivity.
        </div>

      </div>
    </div>
  );
}
